"use client";
import { ReactNode } from "react";
import { iconsMap } from "../icons";
import {
  ContactCardsType,
  LocationInfoType,
  SocialLinkType,
} from "./contacts-section";
import Link from "next/link";
import { Button } from "../ui/button";
import { Reveal } from "../reveal";

/* ------------------ Social ------------------ */

interface SocialPlatform {
  platform: "facebook" | "instagram" | "linkedin" | "whatsapp" | "twitter";
  icon: ReactNode;
  url: string;
  handle: string | null;
}

interface SocialCardProps {
  icon: ReactNode;
  heading: string;
  description: string;
  platforms: SocialPlatform[];
}

function adaptPlatformsToCard(platforms?: SocialLinkType): SocialPlatform[] {
  if (!platforms) return [];

  return platforms
    .filter((platform) => platform?.platform && platform?.icon)
    .map((platform) => ({
      platform: platform.platform,
      icon: iconsMap[platform.icon],
      url: platform.url,
      handle: platform.handle,
    }));
}

function SocialCard({
  icon,
  heading,
  description,
  platforms,
}: SocialCardProps) {
  return (
    <Reveal className="border-[0.5px] border-[#A5A7AA] bg-[#E6EBF333] rounded-lg p-6 flex flex-col gap-6 justify-start items-start">
      <div className="border-[0.2px] border-[#A5A7AA] rounded-sm p-2">
        {icon}
      </div>
      <div className="flex flex-col gap-1 justify-start items-start">
        <h2 className="text-[#121212] text-2xl">{heading}</h2>
        {description && <p className="text-[#666666] text-sm">{description}</p>}

        {platforms.length > 0 && (
          <div className="flex flex-row gap-4 mt-3">
            {platforms.map((platform, index) => (
              <Link
                key={index}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-2xl"
              >
                {platform.icon}
              </Link>
            ))}
          </div>
        )}
      </div>
    </Reveal>
  );
}

/* ------------------ Location ------------------ */

interface PhoneNumber {
  label: string;
  number: string;
  isPrimary: boolean;
}

interface LocationCardProps extends Omit<SocialCardProps, "platforms"> {
  address?: string;
  googleMapsLink?: string;
  phoneNumbers: PhoneNumber[];
  businessHours?: string;
}

function adaptLocationInfoToCard(locationInfo?: LocationInfoType) {
  if (!locationInfo) {
    return {
      address: undefined,
      googleMapsLink: undefined,
      businessHours: undefined,
      phoneNumbers: [],
    };
  }

  return {
    address: locationInfo.address || undefined,
    googleMapsLink: locationInfo.googleMapsLink || undefined,
    businessHours: locationInfo.businessHours || undefined,
    phoneNumbers:
      locationInfo.phoneNumbers?.map((phoneNumber) => ({
        ...phoneNumber,
        isPrimary: phoneNumber.isPrimary || false,
      })) || [],
  };
}

function LocationCard({
  icon,
  heading,
  description,
  address,
  googleMapsLink,
  businessHours,
  phoneNumbers,
}: LocationCardProps) {
  return (
    <Reveal className="border-[0.5px] border-[#A5A7AA] bg-[#E6EBF333] rounded-lg p-6 flex flex-col gap-6 justify-start items-start">
      <div className="border-[0.2px] border-[#A5A7AA] rounded-sm p-2">
        {icon}
      </div>
      <div className="flex flex-col gap-1 justify-start items-start">
        <h2 className="text-[#121212] text-2xl">{heading}</h2>
        {description && <p className="text-[#666666] text-sm">{description}</p>}
        {address && <p className="text-[#666666] text-sm">{address}</p>}

        {googleMapsLink && (
          <Button
            variant="link"
            className="px-0 underline text-base mt-3 text-start"
            asChild
          >
            <Link
              href={googleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              View on Google Maps
            </Link>
          </Button>
        )}

        {phoneNumbers.length > 0 && (
          <div>
            {phoneNumbers.map((phone, index) => (
              <div key={index}>
                <Button
                  variant="link"
                  className="px-0 underline text-base mt-3 text-start"
                  asChild
                >
                  <Link href={`tel:${phone.number.replace(/\s+/g, "")}`}>
                    <span>{phone.number}</span>{" "}
                    {phone.label && <span>({phone.label})</span>}{" "}
                    {phone.isPrimary && <strong>Primary</strong>}
                  </Link>
                </Button>
              </div>
            ))}
          </div>
        )}

        {businessHours && (
          <div>
            <span>Hours: </span>
            <span>{businessHours}</span>
          </div>
        )}
      </div>
    </Reveal>
  );
}

/* ------------------ Support ------------------ */

interface SupportCardProps {
  icon: ReactNode;
  heading: string;
  description: string;
  link?: string;
  linkText?: string;
}

function SupportCard({
  icon,
  heading,
  description,
  link,
  linkText,
}: SupportCardProps) {
  return (
    <Reveal className="border-[0.5px] border-[#A5A7AA] bg-[#E6EBF333] rounded-lg p-6 flex flex-col gap-6 justify-start items-start">
      <div className="border-[0.2px] border-[#A5A7AA] rounded-sm p-2">
        {icon}
      </div>
      <div className="flex flex-col justify-start items-start">
        <h2 className="text-[#121212] text-2xl">{heading}</h2>
        {description && (
          <p className="text-[#666666] text-sm px-0">{description}</p>
        )}

        {link && (
          <Button
            variant="link"
            className="px-0 underline text-base mt-2 text-start"
            asChild
          >
            <Link href={link} target="_blank" rel="noopener noreferrer">
              {linkText || link || "Get Support"}
            </Link>
          </Button>
        )}
      </div>
    </Reveal>
  );
}

/* ------------------ Dispatcher ------------------ */

export function ContactCards({ data }: { data: ContactCardsType }) {
  switch (data.contactType) {
    case "social":
      return (
        <SocialCard
          icon={iconsMap[data.icon || "chat"]}
          heading={data.heading}
          description={data.description}
          platforms={adaptPlatformsToCard(data.socialLinks)}
        />
      );
    case "location":
      return (
        <LocationCard
          icon={iconsMap[data.icon || "chat"]}
          heading={data.heading}
          description={data.description}
          {...adaptLocationInfoToCard(data.locationInfo)}
        />
      );
    case "support":
      return (
        <SupportCard
          icon={iconsMap[data.icon || "chat"]}
          heading={data.heading}
          description={data.description}
          link={data.supportLink || undefined}
          linkText={data.supportLinkText || undefined}
        />
      );
  }
}
